import { FAN_FICTION_CATEGORY_ID } from "@/constants/themeMatching";
import { FEEDBACK_THEME_REQUIRED_FIELDS, THEME_ASSIST_MAX_TOTAL_CHARS, THEME_FIELD_NAMES } from "@/constants/themeReview";
import type { Novel } from "@/lib/models";
import type { Detail } from "@/lib/models/theme";

export type ThemeAssistHomeKind = "feedback" | "matching";

export type ThemeAssistHomeValidationError =
  | "fanFictionNotSupported"
  | "missingNovelSettings"
  | "missingSynopsis"
  | "exceededMaxCharacters"
  | "missingTheme";

export function themeAssistTotalCharacters(details: Detail[] | undefined): number {
  if (!details?.length) {
    return 0;
  }
  return THEME_FIELD_NAMES.reduce((sum, name) => {
    const value = details.find((detail) => detail.name === name)?.value ?? "";
    return sum + value.length;
  }, 0);
}

export function validateThemeAssistHome(options: {
  novel: Novel | null | undefined;
  details: Detail[] | undefined;
  kind: ThemeAssistHomeKind;
}): ThemeAssistHomeValidationError | null {
  const { novel, details, kind } = options;
  if (!novel) {
    return "missingNovelSettings";
  }

  if (kind === "matching" && novel.category?.includes(FAN_FICTION_CATEGORY_ID)) {
    return "fanFictionNotSupported";
  }

  const hasGenre = !!novel.genre?.trim();
  const hasCategory = !!novel.category?.some((item) => item.trim());
  if (!hasGenre || !hasCategory) {
    return "missingNovelSettings";
  }

  if (!details) {
    return "missingTheme";
  }

  const hasMissingRequired = FEEDBACK_THEME_REQUIRED_FIELDS.some((name) => {
    const value = details.find((detail) => detail.name === name)?.value;
    return !value || !value.trim();
  });
  if (hasMissingRequired) {
    return "missingSynopsis";
  }

  if (themeAssistTotalCharacters(details) > THEME_ASSIST_MAX_TOTAL_CHARS) {
    return "exceededMaxCharacters";
  }

  return null;
}
